import apiClient from './client';
import { LedgerEntry, CheckAllocationInfo, DepositAllocationInfo } from '../types';

export interface LedgerEntrySummaryRow {
  status: string;
  status_label: string;
  deposits: number;
  payments: number;
  total: number;
}

export interface LedgerEntrySummaryResponse {
  account_id: string | null;
  account_name: string | null;
  rows: LedgerEntrySummaryRow[];
  totals: LedgerEntrySummaryRow;
}

export const ledgerApi = {
  /**
   * List all ledger entries for a client
   */
  listLedgerEntries: async (clientId: string, options?: {
    unallocatedOnly?: boolean;
    accountId?: string | null;
    entryType?: string;
    status?: string | null;
  }): Promise<LedgerEntry[]> => {
    const params = new URLSearchParams();
    if (options?.unallocatedOnly) {
      params.append('unallocated_only', 'true');
    }
    if (options?.accountId) {
      params.append('account_id', options.accountId);
    }
    if (options?.entryType) {
      params.append('entry_type', options.entryType);
    }
    if (options?.status) {
      params.append('status', options.status);
    }
    const queryString = params.toString();
    const response = await apiClient.get<LedgerEntry[]>(
      `/clients/${clientId}/ledger-entries${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  },

  /**
   * Get a specific ledger entry
   */
  getLedgerEntry: async (entryId: string): Promise<LedgerEntry> => {
    const response = await apiClient.get<LedgerEntry>(`/ledger-entries/${entryId}`);
    return response.data;
  },

  /**
   * Update a ledger entry
   */
  updateLedgerEntry: async (
    entryId: string,
    data: Partial<LedgerEntry>
  ): Promise<LedgerEntry> => {
    const response = await apiClient.put<LedgerEntry>(
      `/ledger-entries/${entryId}`,
      data
    );
    return response.data;
  },

  /**
   * Toggle whether a ledger entry is a customer transaction
   */
  toggleIsCustomer: async (clientId: string, entryId: string, isCustomer: boolean): Promise<LedgerEntry> => {
    const response = await apiClient.post<LedgerEntry>(
      `/clients/${clientId}/ledger-entries/${entryId}/toggle-customer`,
      { is_customer: isCustomer }
    );
    return response.data;
  },

  /**
   * Assign a ledger entry to a customer
   */
  assignToCustomer: async (
    entryId: string,
    customerId: string
  ): Promise<LedgerEntry> => {
    const response = await apiClient.post<LedgerEntry>(
      `/ledger-entries/${entryId}/assign-customer`,
      { customer_id: customerId }
    );
    return response.data;
  },

  /**
   * Delete a ledger entry
   */
  deleteLedgerEntry: async (entryId: string): Promise<void> => {
    await apiClient.delete(`/ledger-entries/${entryId}`);
  },

  /**
   * Get allocations for a ledger entry with linked check details
   */
  getCheckAllocations: async (
    clientId: string,
    entryId: string
  ): Promise<CheckAllocationInfo[]> => {
    const response = await apiClient.get<CheckAllocationInfo[]>(
      `/clients/${clientId}/ledger-entries/${entryId}/check-allocations`
    );
    return response.data;
  },

  /**
   * Get allocations for a ledger entry with linked deposit details
   */
  getDepositAllocations: async (
    clientId: string,
    entryId: string
  ): Promise<DepositAllocationInfo[]> => {
    const response = await apiClient.get<DepositAllocationInfo[]>(
      `/clients/${clientId}/ledger-entries/${entryId}/deposit-allocations`
    );
    return response.data;
  },

  /**
   * Get summary counts of ledger entries by status and type
   */
  getLedgerEntrySummary: async (
    clientId: string,
    accountId?: string | null
  ): Promise<LedgerEntrySummaryResponse> => {
    const params = new URLSearchParams();
    if (accountId) {
      params.append('account_id', accountId);
    }
    const queryString = params.toString();
    const response = await apiClient.get<LedgerEntrySummaryResponse>(
      `/clients/${clientId}/ledger-entries/summary${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  },
};
